// src/pages/marketplace/developer/DeveloperDependenciesPage.tsx
// Manage product dependencies for the developer portal.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

const TABS = [
  { key: 'edit', label: 'General', path: (id: string) => `/developer/products/${id}/edit` },
  { key: 'marketplace', label: 'Marketplace', path: (id: string) => `/developer/products/${id}/marketplace` },
  { key: 'dependencies', label: 'Dependencies', path: (id: string) => `/developer/products/${id}/dependencies` },
  { key: 'reviews', label: 'Reviews', path: (id: string) => `/developer/products/${id}/reviews` },
  { key: 'publish', label: 'Publish', path: (id: string) => `/developer/products/${id}/publish` },
] as const;

const DEPENDENCY_TYPES = ['DATA_SOURCE', 'FEATURE', 'INTEGRATION', 'EXTENDS'] as const;
const REQUIREMENT_TYPES = ['HARD', 'SOFT', 'RECOMMENDED'] as const;

interface DependencyForm {
  required_product_id: string;
  dependency_type: string;
  requirement: string;
  description: string;
  degraded_mode_message: string;
  required_features: string;
  minimum_plan_slug: string;
  block_subscription: boolean;
  show_recommendation: boolean;
}

const emptyForm: DependencyForm = {
  required_product_id: '',
  dependency_type: 'DATA_SOURCE',
  requirement: 'HARD',
  description: '',
  degraded_mode_message: '',
  required_features: '',
  minimum_plan_slug: '',
  block_subscription: false,
  show_recommendation: true,
};

function depTypeVariant(type: string): string {
  switch (type) {
    case 'DATA_SOURCE': return 'info';
    case 'FEATURE': return 'primary';
    case 'INTEGRATION': return 'secondary';
    case 'EXTENDS': return 'warning';
    default: return 'secondary';
  }
}

function reqVariant(req: string): string {
  switch (req) {
    case 'HARD': return 'error';
    case 'SOFT': return 'warning';
    case 'RECOMMENDED': return 'info';
    default: return 'secondary';
  }
}

export const DeveloperDependenciesPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useProduct();
  const { t } = useI18n();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<DependencyForm>({ ...emptyForm });
  const [isSaving, setIsSaving] = useState(false);
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [reverseDependencies, setReverseDependencies] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
      loadDependencies();
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const loadDependencies = async () => {
    if (!id) return;
    try {
      const result = await store.ListProductDependencies(id);
      const rawItems = result?.dependencies?.items;
      const items = Array.isArray(rawItems) ? rawItems : [];
      const forward: any[] = [];
      const reverse: any[] = [];
      for (const dep of items) {
        const depProductId = dep.dependent_product?.id;
        if (depProductId === id) {
          forward.push(dep);
        } else {
          reverse.push(dep);
        }
      }
      setDependencies(forward);
      setReverseDependencies(reverse);
    } catch {
      // Error handled by store
    }
  };

  const handleChange = (field: keyof DependencyForm, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      if (editingId) {
        // For edit we delete + recreate
        await store.DeleteProductDependency(editingId);
      }
      await store.CreateProductDependency(id, {
        required_product: { id: formData.required_product_id },
        dependency_type: `PRODUCT_DEPENDENCY_TYPE_${formData.dependency_type}`,
        requirement: `PRODUCT_DEPENDENCY_REQUIREMENT_${formData.requirement}`,
        description: formData.description,
        degraded_mode_message: formData.degraded_mode_message || undefined,
        required_features: formData.required_features
          ? formData.required_features.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        minimum_plan_slug: formData.minimum_plan_slug || undefined,
        block_subscription: formData.block_subscription,
        show_recommendation: formData.show_recommendation,
      } as any);
      setShowAddForm(false);
      setEditingId(null);
      setFormData({ ...emptyForm });
      await loadDependencies();
    } catch {
      // Error handled by store
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (dep: any) => {
    const depId = dep.entity?.id || dep.id;
    setEditingId(depId);
    setFormData({
      required_product_id: dep.required_product?.id || dep.required_product_id || '',
      dependency_type: dep.dependency_type || 'DATA_SOURCE',
      requirement: dep.requirement || 'HARD',
      description: dep.description || '',
      degraded_mode_message: dep.degraded_mode_message || '',
      required_features: Array.isArray(dep.required_features)
        ? dep.required_features.join(', ')
        : (dep.required_features || ''),
      minimum_plan_slug: dep.minimum_plan_slug || '',
      block_subscription: dep.block_subscription ?? false,
      show_recommendation: dep.show_recommendation ?? true,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (depId: string) => {
    if (!confirm('Are you sure you want to delete this dependency?')) return;
    try {
      await store.DeleteProductDependency(depId);
      await loadDependencies();
    } catch {
      // Error handled by store
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ ...emptyForm });
  };

  const productName = (store.currentItem as any)?.name || 'Product';

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb. The tab already names this page; the heading stays for screen readers. */}
      <div>
        <nav className="text-sm">
          <Link to="/developer" className="text-muted-foreground hover:text-foreground">
            Developer
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/developer/products/${id}/edit`} className="text-muted-foreground hover:text-foreground">
            {productName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Dependencies</span>
        </nav>
        <h1 className="sr-only">Dependencies</h1>
      </div>

      {/* Tab navigation */}
      <nav className="flex border-b border-border">
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path(id || '')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab.key === 'dependencies'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Add dependency button */}
      {!showAddForm && (
        <div>
          <Button onClick={() => setShowAddForm(true)}>Add Dependency</Button>
        </div>
      )}

      {/* Add/Edit dependency form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Dependency' : 'Add Dependency'}</CardTitle>
            <CardDescription>
              Define a dependency on another product in the platform.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Required Product ID"
              type="text"
              value={formData.required_product_id}
              onChange={(e) => handleChange('required_product_id', e.target.value)}
              placeholder="Product ID or slug"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Dependency Type
                </label>
                <Select
                  value={formData.dependency_type}
                  onChange={(e) => handleChange('dependency_type', e.target.value)}>
                  {DEPENDENCY_TYPES.map((dt) => (
                    <option key={dt} value={dt}>
                      {dt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Requirement
                </label>
                <Select value={formData.requirement} onChange={(e) => handleChange('requirement', e.target.value)}>
                  {REQUIREMENT_TYPES.map((rt) => (
                    <option key={rt} value={rt}>{rt}</option>
                  ))}
                </Select>
              </div>
            </div>

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Why is this dependency needed?"
              rows={3}
            />

            {formData.requirement === 'SOFT' && (
              <Input
                label="Degraded Mode Message"
                type="text"
                value={formData.degraded_mode_message}
                onChange={(e) => handleChange('degraded_mode_message', e.target.value)}
                placeholder="Message shown when this soft dependency is unavailable"
              />
            )}

            <Input
              label="Required Features"
              type="text"
              value={formData.required_features}
              onChange={(e) => handleChange('required_features', e.target.value)}
              placeholder="feature-a, feature-b"
              helperText="Comma-separated list of required features from the dependency."
            />

            <Input
              label="Minimum Plan Slug"
              type="text"
              value={formData.minimum_plan_slug}
              onChange={(e) => handleChange('minimum_plan_slug', e.target.value)}
              placeholder="pro"
            />

            {formData.requirement === 'HARD' && (
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.block_subscription}
                  onChange={(e) => handleChange('block_subscription', e.target.checked)}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span className="text-sm font-medium text-foreground">
                  Block subscription if dependency not met
                </span>
              </label>
            )}

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.show_recommendation}
                onChange={(e) => handleChange('show_recommendation', e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground">
                Show recommendation to install dependency
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('common.saving') : 'Save'}
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dependencies list */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Product Dependencies</h2>
        {dependencies.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground text-center">
                No dependencies configured. This product has no requirements on other products.
              </p>
            </CardContent>
          </Card>
        ) : (
          dependencies.map((dep: any) => {
            const depId = dep.entity?.id || dep.id;
            const reqProductName = dep.required_product?.name || dep.required_product_id || 'Unknown Product';
            return (
              <Card key={depId}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-semibold text-foreground">
                          {reqProductName}
                        </span>
                        <Badge variant={depTypeVariant(dep.dependency_type) as any}>
                          {(dep.dependency_type || '').replace(/_/g, ' ')}
                        </Badge>
                        <Badge variant={reqVariant(dep.requirement) as any}>
                          {dep.requirement || 'HARD'}
                        </Badge>
                      </div>
                      {dep.description && (
                        <p className="text-sm text-muted-foreground mt-1">{dep.description}</p>
                      )}
                      {dep.degraded_mode_message && (
                        <p className="text-xs text-warning mt-1">
                          Degraded: {dep.degraded_mode_message}
                        </p>
                      )}
                      {dep.required_features && dep.required_features.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(Array.isArray(dep.required_features)
                            ? dep.required_features
                            : [dep.required_features]
                          ).map((f: string, i: number) => (
                            <Badge key={i} variant="secondary" size="sm">{f}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(dep)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDelete(depId)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Reverse dependencies */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Products That Depend on This Product</h2>
        {reverseDependencies.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground text-center">
                No other products depend on this product.
              </p>
            </CardContent>
          </Card>
        ) : (
          reverseDependencies.map((dep: any) => {
            const depId = dep.entity?.id || dep.id;
            const depProductName = dep.dependent_product?.name || dep.dependent_product_id || 'Unknown Product';
            return (
              <Card key={depId}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">
                      {depProductName}
                    </span>
                    <Badge variant={depTypeVariant(dep.dependency_type) as any}>
                      {(dep.dependency_type || '').replace(/_/g, ' ')}
                    </Badge>
                    <Badge variant={reqVariant(dep.requirement) as any}>
                      {dep.requirement || 'HARD'}
                    </Badge>
                  </div>
                  {dep.description && (
                    <p className="text-sm text-muted-foreground mt-1">{dep.description}</p>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
});

export default DeveloperDependenciesPage;
